import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  HoverCard,
  Image,
  Stack,
  Text,
  TextInput,
  Tooltip,
  VisuallyHidden,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { IconHierarchy3, IconPaperclip, IconPhoto, IconSearch, IconStar, IconX } from '@tabler/icons-react'
import { useMutation } from '@tanstack/react-query'
import type { MRT_ColumnDef } from 'mantine-react-table'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { confirmLiveModuleAction, LiveModuleBanner } from '@/components/LiveModuleBanner'
import { Table } from '@/components/Table'
import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { StepWithValidation } from '@/hooks/useStepsWithValidation'
import { AnalyzingBadge } from '@/screens/ContentModuleFormScreen/AnalyzingBadge'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { ReviewStepModal } from '@/screens/ContentModuleFormScreen/ReviewStepModal'
import { UploadStepModal } from '@/screens/ContentModuleFormScreen/UploadStepModal'
import type { GetStepsResponse } from '@/services/api/api.types'
import { castStepToZodForm, getProcessErrorMessage, splitMediaSteps } from '@/utils/contentModuleForm'

export type StepsSectionMode = 'all' | 'primary' | 'supplementary'

type StepsSectionProps = CMFormSection & {
  /** 'all' (default) | 'primary' (first step only) | 'supplementary' (rest). */
  mode?: StepsSectionMode
}

export const StepsSection = memo(function StepsSection(props: StepsSectionProps) {
  const mode: StepsSectionMode = props.mode ?? 'all'
  const isPrimaryMode = mode === 'primary'
  const isSupplementaryMode = mode === 'supplementary'

  const [localSteps, setLocalSteps] = useState<StepWithValidation[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const pendingReordersRef = useRef(0)
  const prevStepCountRef = useRef(0)
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const { api } = useGlobalState()

  const { primary: primaryStep, supplementary: supplementarySteps } = useMemo(
    () => splitMediaSteps(localSteps),
    [localSteps],
  )

  const displaySteps = useMemo<StepWithValidation[]>(() => {
    if (isPrimaryMode) return primaryStep ? [primaryStep] : []
    if (isSupplementaryMode) return supplementarySteps
    return localSteps
  }, [localSteps, primaryStep, supplementarySteps, isPrimaryMode, isSupplementaryMode])

  const hasPrimary = !!primaryStep
  const hasDisplayed = displaySteps.length > 0

  const scrollToBottom = useCallback(() => {
    const el = tableWrapperRef.current?.querySelector('.steps-scroll-container') as HTMLElement | null
    if (el) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight
      })
    }
  }, [])

  useEffect(() => {
    if (props.stepsQuery.data && pendingReordersRef.current === 0) {
      setLocalSteps(props.stepsQuery.data)
    }
  }, [props.stepsQuery.data])

  useEffect(() => {
    if (props.stepSavedTrigger && props.stepSavedTrigger > 0) {
      scrollToBottom()
    }
  }, [props.stepSavedTrigger, scrollToBottom])

  useEffect(() => {
    const count = displaySteps.length
    if (count > 0 && prevStepCountRef.current === 0) {
      scrollToBottom()
    }
    prevStepCountRef.current = count
  }, [displaySteps.length, scrollToBottom])

  const reorderStepsMutation = useMutation({
    mutationFn: api.reorderSteps,
    onSuccess: () => {
      props.stepsQuery.refetch().finally(() => {
        pendingReordersRef.current--
      })
    },
    onError: error => {
      pendingReordersRef.current--
      console.error('Failed to reorder steps:', error)
      if (props.stepsQuery.data) {
        setLocalSteps(props.stepsQuery.data)
      }
    },
  })

  const modalStyles = {
    styles: {
      body: {
        paddingTop: theme.rem(16),
        backgroundColor: theme.colors.gray[0],
      },
    },
  }

  function openUploadModal() {
    props.openModal?.({
      Component: UploadStepModal,
      ModalProps: modalStyles,
      ComponentProps: { isPrimary: isPrimaryMode },
    })
  }

  function openReviewModal() {
    props.openModal?.({
      Component: ReviewStepModal,
      ModalProps: modalStyles,
    })
  }

  const addNewContent = async () => {
    if (props.isLive) {
      const confirmed = await confirmLiveModuleAction({
        body: 'When you save this new file, it will immediately be available to your End Users in the app. Do you want to continue?',
      })
      if (!confirmed) return
    }
    stageStep()
    openUploadModal()
  }

  // Supplementary mode reorders only the subset; prepend the primary so the
  // API receives the full ordered list.
  const handleRowOrderChange = (newData: StepWithValidation[]) => {
    if (!props.taskId) return

    const fullList: StepWithValidation[] = isSupplementaryMode
      ? [...(primaryStep ? [primaryStep] : []), ...newData]
      : newData

    pendingReordersRef.current++
    setLocalSteps(fullList)

    reorderStepsMutation.mutate({
      task_id: props.taskId,
      step_ids: fullList.map(step => step.stepId),
    })
  }

  async function deleteStep(data: GetStepsResponse['0']) {
    modals.openConfirmModal({
      title: props.isLive ? undefined : 'Delete content?',
      withCloseButton: !props.isLive,
      centered: true,
      children: props.isLive ? (
        <Stack gap="md">
          <LiveModuleBanner />
          <Text size="sm">
            Deleting this file will immediately remove it for End Users in the app. This action cannot be undone.
          </Text>
        </Stack>
      ) : (
        <Text size="sm">
          Are you sure you want to delete this content? This action is destructive and you will lose all of the progress
          made for this content.
        </Text>
      ),
      labels: { confirm: 'Delete Content', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      groupProps: { mt: 'xl' },
      onConfirm: () => {
        props.stepForm.setValues(castStepToZodForm())
        props.stepDeleteMutation?.mutate({ step_id: data.stepId, task_id: props.taskId! })
      },
    })
  }

  function stageStep(data?: GetStepsResponse['0']) {
    props.stepForm.setValues(castStepToZodForm(data))
    props.stepForm.resetDirty()
    props.stepForm.resetTouched()
  }

  const emptyStateProps = isPrimaryMode
    ? {
        title: 'No Primary Media File',
        description: 'Upload the main media file for this module to get started.',
        Icon: IconStar,
      }
    : isSupplementaryMode
      ? {
          title: 'No Supplementary Content',
          description: 'Add additional files to enrich this module.',
          Icon: IconPaperclip,
        }
      : {
          title: 'No Files Added',
          description: 'Build the outline for your module and add content files.',
          Icon: IconHierarchy3,
        }

  // Primary: single-slot, hide once filled. Supplementary: requires a primary first.
  const showAddButton = isPrimaryMode ? !hasPrimary : isSupplementaryMode ? hasPrimary : true

  const addButtonLabel = isPrimaryMode
    ? 'Add Primary Media File'
    : isSupplementaryMode
      ? hasDisplayed
        ? 'Add Another File'
        : 'Add Supplementary File'
      : hasDisplayed
        ? 'Add Another File'
        : 'Add File'

  return (
    <Stack gap="xxxl">
      <Box ref={tableWrapperRef}>
        {hasDisplayed && !isPrimaryMode && (
          <Group justify="flex-end" mb="xs">
            <TextInput
              size="xs"
              placeholder="Search by file name or type..."
              leftSection={<IconSearch size={14} stroke={1.5} />}
              rightSection={
                searchQuery ? (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size="xs"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search">
                    <IconX size={12} aria-hidden="true" />
                  </ActionIcon>
                ) : null
              }
              value={searchQuery}
              onChange={e => setSearchQuery(e.currentTarget.value)}
              w={260}
            />
          </Group>
        )}
        {hasDisplayed && (
          <Table
            data={displaySteps}
            columns={stepColDefs}
            rowActionsInclude={['edit', 'delete']}
            enableTopToolbar={false}
            enableGlobalFilter={!isPrimaryMode}
            {...(!isPrimaryMode && { state: { globalFilter: searchQuery }, onGlobalFilterChange: setSearchQuery })}
            enableRowOrdering={!isPrimaryMode && !searchQuery}
            enableRowVirtualization={true}
            enableStickyHeader={true}
            enablePagination={false}
            mantineTableContainerProps={{ style: { maxHeight: '60vh' }, className: 'steps-scroll-container' }}
            rowVirtualizerOptions={{ overscan: 5 }}
            onRowActionClick={async (action, row) => {
              const hasError = !!row.original.processProgressError
              if (action === 'delete') {
                if (isPrimaryMode && !hasError) return
                deleteStep(row.original)
              } else if (action === 'edit') {
                const isProcessing = row.original.inProcess === true
                if (isProcessing || hasError) return
                if (props.isLive) {
                  const confirmed = await confirmLiveModuleAction({
                    body: 'Edits to this file will be immediately available to your End Users in the app. Do you want to continue?',
                  })
                  if (!confirmed) return
                }
                stageStep(row.original)
                openReviewModal()
              }
            }}
            rowActionsTooltipLabels={row => {
              const hasError = !!row.original.processProgressError
              return {
                edit: hasError
                  ? 'This file failed to process. Delete it and upload again.'
                  : row.original.inProcess
                    ? 'Content is still being analyzed'
                    : 'Review File',
                delete:
                  isPrimaryMode && !hasError ? 'The Primary Media File cannot be deleted at this time.' : 'Delete File',
              }
            }}
            rowActionsButtonProps={row => {
              const hasError = !!row.original.processProgressError
              return {
                edit: { disabled: row.original.inProcess === true || hasError },
                delete: { disabled: isPrimaryMode && !hasError },
              }
            }}
            EmptyStateProps={emptyStateProps}
            displayColumnDefOptions={{ 'mrt-row-actions': { size: 98 } }}
            onRowOrderChange={handleRowOrderChange}
          />
        )}

        {showAddButton && (
          <Group justify="center" mt="xl">
            <Stack gap="xs" align="center">
              <Button
                loading={
                  props.cmContentQuery.isFetching ||
                  props.cmCreateMutation.isPending ||
                  props.stepCreateMutation.isPending
                }
                onClick={() => {
                  addNewContent()
                }}>
                {addButtonLabel}
              </Button>
            </Stack>
          </Group>
        )}

        {isSupplementaryMode && !hasPrimary && (
          <Group justify="center" mt="xl">
            <Text size="sm" c="dimmed">
              Add a Primary Media File first to enable supplementary content.
            </Text>
          </Group>
        )}
      </Box>

      {/* TODO: Re-enable when we have multiple sections */}
      {/* <Grid gutter="xxxl">
        <Grid.Col span={12}>
          <Radio.Group label="Module Flow" withAsterisk {...props.moduleForm.getInputProps('presentationMode')}>
            <Group gap="xl" align="stretch">
              <RadioCard
                value={moduleSchema.shape.presentationMode.enum.member_directed}
                label="User Directed"
                description="Users can access the content in any order."
              />

              <RadioCard
                value={moduleSchema.shape.presentationMode.enum.sequential}
                label={<ComingSoon prefix="Sequential " dimPrefix />}
                disabled
                description="Their first time through, the user must go through the content in the order it's laid out."
              />
            </Group>
          </Radio.Group>
        </Grid.Col>
      </Grid> */}
    </Stack>
  )
})

const UPPERCASE_TYPES = new Set(['url', 'pdf', 'pptx', 'docx'])
const SPECIAL_CASE_TYPES: Record<string, string> = { youtube: 'YouTube' }

function formatContentType(type?: string): string {
  if (!type) return '—'
  if (SPECIAL_CASE_TYPES[type]) return SPECIAL_CASE_TYPES[type]
  if (UPPERCASE_TYPES.has(type)) return type.toUpperCase()
  return type.charAt(0).toUpperCase() + type.slice(1)
}

const THUMBNAIL_CELL_BOX = {
  width: 56,
  height: 32,
  borderRadius: 6,
  overflow: 'hidden',
  border: '1px solid var(--mantine-color-gray-3)',
  backgroundColor: 'var(--mantine-color-gray-1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const

const THUMBNAIL_CELL_BOX_INTERACTIVE = { ...THUMBNAIL_CELL_BOX, cursor: 'zoom-in' } as const

function ThumbnailCell({ thumbnail, label }: { thumbnail?: string | null; label?: string | null }) {
  const { configStore } = useGlobalState()

  if (!thumbnail) {
    return (
      <Box style={THUMBNAIL_CELL_BOX}>
        <IconPhoto size={16} stroke={1.5} color="var(--mantine-color-gray-5)" />
      </Box>
    )
  }

  const src = `${configStore.serverBaseUrl}/files/${thumbnail}`
  const alt = label ? `Thumbnail for ${label}` : 'File thumbnail'

  return (
    <HoverCard openDelay={150} closeDelay={50} position="right" withArrow shadow="md" withinPortal>
      <HoverCard.Target>
        <Box style={THUMBNAIL_CELL_BOX_INTERACTIVE}>
          <Image src={src} alt={alt} w={56} h={32} fit="cover" />
        </Box>
      </HoverCard.Target>
      <HoverCard.Dropdown p={4}>
        <Image src={src} alt={alt} w={320} h={180} fit="cover" radius="sm" />
      </HoverCard.Dropdown>
    </HoverCard>
  )
}

const stepColDefs: MRT_ColumnDef<StepWithValidation>[] = [
  {
    id: 'thumbnail',
    header: 'Thumbnail',
    accessorKey: 'thumbnail',
    Header: () => <VisuallyHidden>Thumbnail</VisuallyHidden>,
    Cell: props => <ThumbnailCell thumbnail={props.row.original.thumbnail} label={props.row.original.name} />,
    grow: false,
    size: 72,
    enableGlobalFilter: false,
  },
  {
    accessorKey: 'name',
    header: 'File Name',
    Cell: props => (
      <Text size="sm" fw={500}>
        {props.renderedCellValue || 'Untitled'}
      </Text>
    ),
    size: 280,
    grow: true,
  },
  {
    id: 'contentType',
    header: 'File Type',
    accessorFn: row => formatContentType(row.contentType),
    grow: false,
    size: 100,
  },
  // {
  //   accessorKey: 'title',
  //   header: 'Section Overview',
  //   Cell: props => <Text size="sm">{props.renderedCellValue}</Text>,
  //   grow: true,
  // },
  {
    id: 'status',
    header: 'File Status',
    grow: false,
    size: 158,
    Cell: props => {
      const { inProcess, processProgress, processProgressError, transcodeCompleted, transcodeSuccess, valid } =
        props.row.original

      if (processProgressError) {
        return (
          <Tooltip label={getProcessErrorMessage(processProgressError)} withArrow position="top" multiline maw={300}>
            <Badge color="red" variant="filled">
              Error
            </Badge>
          </Tooltip>
        )
      }

      if (inProcess) {
        return <AnalyzingBadge progress={processProgress} />
      }

      if (transcodeCompleted === false) {
        return (
          <Badge color="yellow" variant="filled">
            Preparing
          </Badge>
        )
      }

      if (transcodeCompleted === true && transcodeSuccess === false) {
        return (
          <Badge color="red" variant="filled">
            Transcode Error
          </Badge>
        )
      }

      if (!valid) {
        return (
          <Badge color="orange" variant="filled">
            Review Needed
          </Badge>
        )
      }

      return (
        <Badge color="green" variant="filled">
          Ready to Submit
        </Badge>
      )
    },
  },
]
